import { useState } from 'react';
import { motion } from 'motion/react';
import { LogOut, LayoutDashboard, ShoppingCart, CreditCard, UploadCloud, Menu, X, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { ThemeToggle } from '../components/ThemeToggle';

export const CustomerDashboard = () => {
  const [activeTab, setActiveTab] = useState('پیشخوان');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  
  const { user, logoutState } = useAuth();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const handleLogout = () => {
    logoutState();
    navigate('/login');
  };

  const username = user?.username || 'مشتری';

  const menuItems = [
    { name: 'پیشخوان', icon: LayoutDashboard },
    { name: 'ثبت سفارش (اکسل)', icon: UploadCloud },
    { name: 'پیگیری سفارشات', icon: ShoppingCart },
    { name: 'پرداخت‌ها و مالی', icon: CreditCard },
    { name: 'مشاهده کالاها', icon: Package },
  ];

  return (
    <div dir="rtl" className="min-h-screen bg-[#f0f2f5] dark:bg-slate-900 flex transition-colors duration-300 font-sans text-slate-900 dark:text-slate-100">
      {/* Sidebar */}
      <motion.aside 
        initial={false}
        animate={{ width: isSidebarOpen ? 280 : 88 }}
        className="bg-emerald-900 dark:bg-slate-950 text-white flex flex-col p-6 sticky top-0 h-screen shadow-xl z-20"
      >
        <div className="flex items-center justify-between mb-10 overflow-hidden whitespace-nowrap">
          {isSidebarOpen && (
            <div className="flex items-center space-x-3 space-x-reverse">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center shrink-0">
                <span className="font-bold text-xl">C</span>
              </div>
              <span className="text-xl font-bold tracking-tight">پنل مشتری</span>
            </div>
          )}
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors mr-auto text-slate-300"
          >
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="flex-1 space-y-2 overflow-hidden">
          {menuItems.map((item) => (
            <button
              key={item.name}
              onClick={() => setActiveTab(item.name)}
              className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all ${
                activeTab === item.name 
                ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20' 
                : 'text-emerald-200 hover:bg-white/10 hover:text-white'
              }`}
            >
              <item.icon size={20} className="shrink-0" />
              {isSidebarOpen && <span className="font-medium whitespace-nowrap">{item.name}</span>}
            </button>
          ))}
        </nav>

        <button
          onClick={handleLogout}
          className="mt-auto flex items-center gap-4 p-3 text-red-300 hover:bg-red-500/20 hover:text-red-200 rounded-xl transition-all overflow-hidden"
        >
          <LogOut size={20} className="shrink-0" />
          {isSidebarOpen && <span className="font-medium whitespace-nowrap">خروج از حساب</span>}
        </button>
      </motion.aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        <header className="flex justify-between items-center mb-10">
          <div>
            <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-1">{activeTab}</h2>
            <p className="text-slate-500 dark:text-slate-400">خوش آمدید، {username}</p>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle isDark={isDark} toggle={toggleTheme} />
            <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 flex items-center justify-center font-bold shadow-sm">
              {username.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {[
            { label: 'سفارشات فعال', value: '۳', trend: 'در حال بررسی' },
            { label: 'تعداد کل سفارشات', value: '۱۵', trend: '+۲' },
            { label: 'موجودی کیف پول', value: '۰ ریال', trend: 'شارژ کنید' },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700"
            >
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">{stat.label}</p>
              <div className="flex items-end justify-between">
                <p className="text-3xl font-extrabold text-slate-800 dark:text-white">{stat.value}</p>
                <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2 py-1 rounded-lg">{stat.trend}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden"
        >
          <div className="p-6 border-b border-slate-100 dark:border-slate-700/50 flex justify-between items-center">
            <h3 className="font-bold text-xl text-slate-800 dark:text-white">جدیدترین سفارشات روی سیستم</h3>
            <button className="text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:underline">مشاهده همه</button>
          </div>
          <div className="divide-y divide-slate-50 dark:divide-slate-700/50 flex flex-col items-center justify-center py-10">
             <ShoppingCart size={48} className="text-slate-200 dark:text-slate-700 mb-4" />
             <p className="text-slate-500 dark:text-slate-400 text-sm">سفارشی جهت نمایش وجود ندارد، از طریق فایل اکسل آپلود کنید.</p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};
